// src/components/Common/MoodleEventBooking/MoodleAgendaTable.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { HbrDomTable, HbrForm, HbrInput } from '@harbor/elements-react-standalone';
import { MoodleAgendaItem } from '../../../types/moodleEventBooking';
import { renderErrorMessage } from '../../security-toolbox-ui/security-toolbox-ui-utils';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbModal from '../../security-toolbox-ui/StbModal';


type MoodleAgendaTableProps = {
  agenda: MoodleAgendaItem[];
  onChange: (agenda: MoodleAgendaItem[]) => void;
  eventId?: number | string;
  eventStartLocal: string; // 'YYYY-MM-DDTHH:mm'
  eventEndLocal: string;   // 'YYYY-MM-DDTHH:mm'
};

function toHHMM(localYmdHm: string): string {
  if (!localYmdHm || !localYmdHm.includes('T')) return '00:00';
  const parts = localYmdHm.split('T')[1];
  const [hh, mm] = (parts || '').split(':');
  const HH = String(hh ?? '00').padStart(2, '0');
  const MM = String(mm ?? '00').padStart(2, '0');
  return `${HH}:${MM}`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = (hhmm || '').split(':').map((v) => parseInt(v, 10));
  const hh = isNaN(h) ? 0 : h;
  const mm = isNaN(m) ? 0 : m;
  return hh * 60 + mm;
}

function minutesToHHMM(mins: number): string {
  const clamped = Math.max(0, Math.min(mins, 23 * 60 + 59));
  const hh = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function sanitizeTime(value: string, fallback: string): string {
  const v = String(value || '').trim();
  const m = v.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return fallback;
  const hh = Math.max(0, Math.min(parseInt(m[1], 10), 23));
  const mm = Math.max(0, Math.min(parseInt(m[2], 10), 59));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function MoodleAgendaTable({
  agenda,
  onChange,
  eventId,
  eventStartLocal,
  eventEndLocal,
}: MoodleAgendaTableProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<MoodleAgendaItem>>({});
  const [titleTouched, setTitleTouched] = useState(false);

  const [tempId, setTempId] = useState(-1);
  const [timeError, setTimeError] = useState<string>('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [rows, setRows] = useState<MoodleAgendaItem[]>(agenda || []);
  useEffect(() => {
    setRows(agenda || []);
  }, [agenda]);

  const columns = useMemo(
    () => [
      { id: 'title', prop: 'title', name: 'Title', size: 200 },
      { id: 'time', prop: 'time', name: 'Time', size: 120 },
      { id: 'description', prop: 'description', name: 'Description', size: 400 },
      { id: 'actions', prop: 'actions', name: '', size: 60 },
    ],
    []
  );

  const startHHMM = useMemo(() => toHHMM(eventStartLocal), [eventStartLocal]);
  const endHHMM = useMemo(() => toHHMM(eventEndLocal), [eventEndLocal]);
  const startMin = useMemo(() => hhmmToMinutes(startHHMM), [startHHMM]);
  const endMin = useMemo(() => hhmmToMinutes(endHHMM), [endHHMM]);

// Return the next 5-min interval after the last agenda item, or the next whole hour after now, clamped to event bounds
  const computeDefaultTime = () => {
    if (!rows || rows.length === 0) {
      // Default: next upcoming whole hour, clamped to event bounds
      const now = new Date();
      const start = eventStartLocal;
      const useDate = start ? new Date(start) : now;
      const nextHour = new Date(useDate);
      nextHour.setMinutes(0, 0, 0);
      if (now > nextHour) {
        nextHour.setHours(now.getHours() + 1);
      }
      const hh = String(nextHour.getHours()).padStart(2, '0');
      return `${hh}:00`;
    } else {
      // After last agenda, next 5-min slot
      const last = rows[rows.length - 1]?.time || toHHMM(eventStartLocal);
      const mins = hhmmToMinutes(last);
      let next = mins + 5 - (mins % 5 || 5); // next 5-min
      if (next <= mins) next += 5;
      // Clamp to event start/end
      next = Math.max(startMin, Math.min(next, endMin));
      return minutesToHHMM(next);
    }
  };

  const handleOpenAdd = () => {
    const defTime = computeDefaultTime();
    setForm({ title: '', time: defTime, description: '' });
    setTimeError('');
    setTitleTouched(false);
    setModalOpen(true);
  };

  const validateTime = (hhmm: string): string => {
    const mins = hhmmToMinutes(hhmm);
    if (mins < startMin) {
      return `Selected time is too early (before ${startHHMM}).`;
    }
    if (mins > endMin) {
      return `Selected time is too late (after ${endHHMM}).`;
    }
    return '';
  };

  // IMPORTANT: Harbor components emit onHbr-input/onHbr-submit. Use those to capture changes/submissions.
  const handleFormChange = (e: any) => {
    const name = e?.detail?.name ?? e?.target?.name;
    const value = e?.detail?.value ?? e?.target?.value ?? '';
    if (!name) return;

    if (name === 'time') {
      const hhmm = sanitizeTime(String(value), startHHMM);
      setForm((f) => ({ ...f, time: hhmm }));
      setTimeError(validateTime(hhmm));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const addAgendaItem = async () => {
    const title = (form.title ?? '').toString().trim();
    const timeStr = sanitizeTime((form.time ?? startHHMM) as string, startHHMM);
    const description = (form.description ?? '').toString().trim();

    if (!title || !timeStr) return;

    const err = validateTime(timeStr);
    if (err) {
      setTimeError(err);
      return;
    }

    const next: MoodleAgendaItem = {
      id: tempId,
      title,
      time: timeStr, // store clean 'HH:MM' string
      description,
    };

    const updated = [...rows, next];
    setRows(updated);
    onChange(updated);
    setTempId((id) => id - 1);
  };

  const requestDelete = (id: number) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteId == null) return;
    const updated = rows.filter((r) => r.id !== deleteId);
    setRows(updated);
    onChange(updated);
    setDeleteId(null);
  };

  const cancelDelete = async () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ flex: 1, margin: 0 }}>Agenda</h3>
        <StbButton
          onClick={handleOpenAdd}
          role="add"
          variant="text"
          size="medium"
          className="mt-3"
        >
          Add Agenda Item
        </StbButton>
      </div>

      {columns.length > 0 && rows.length > 0 ? (
        <HbrDomTable autoLayout={true} columns={columns} data={rows}>
          {columns.map((col) => (
            <div
              key={`column-${col.id}`}
              slot={`column-${col.id}`}
              style={{ fontWeight: 600 }}
            >
              {col.name}
            </div>
          ))}
          {rows.map((row) => (
            <React.Fragment key={`row-${String(row.id)}`}>
              <div
                slot={`row-${String(row.id)}-column-title`}
              >
                {row.title}
              </div>
              <div
                slot={`row-${String(row.id)}-column-time`}
              >
                {row.time}
              </div>
              <div
                slot={`row-${String(row.id)}-column-description`}
              >
                {row.description || '—'}
              </div>
              <div
                slot={`row-${String(row.id)}-column-actions`}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <StbButton
                  role="delete"
                  iconOnly={true}
                  variant="text"
                  onClick={() => requestDelete(row.id)}
                  aria-label={`Delete agenda item "${row.title}"`}
                />
              </div>
            </React.Fragment>
          ))}
        </HbrDomTable>
      ) : (
        <div className="text-sm italic text-gray-500 mt-2">No agenda items.</div>
      )}

      {/* Add item modal */}
      <StbModal
        open={modalOpen}
        onClose={async () => {
          setModalOpen(false);
          setTitleTouched(false);
          setForm({});
        }}
        title="Add Agenda Item"
        onSubmit={addAgendaItem}
        role="save"
        submitDisabled={!form.title || Boolean(timeError)}
        submitLabel="Add"
      >
        <HbrForm>
          <HbrInput
            required
            name="title"
            value={form.title ?? ''}
            onHbr-input={(e) => {
              handleFormChange(e);
              setTitleTouched(true);
            }}
          >
            <div slot="label">Title <em className="text-gray-500">(required)</em></div>
            {renderErrorMessage(titleTouched && !form.title, 'Please enter a title')}
          </HbrInput>
          <HbrInput
            required
            type="time"
            name="time"
            value={(form.time as string) ?? startHHMM}
            min={startHHMM}
            max={endHHMM}
            onHbr-input={handleFormChange}
          >
            <div slot="label">{`Time (${startHHMM}–${endHHMM})`} <em className="text-gray-500">(required)</em></div>
            {renderErrorMessage(timeError && timeError !== '', timeError)}
          </HbrInput>
          <HbrInput name="description" label="Description" value={form.description ?? ''} onHbr-input={handleFormChange} />
        </HbrForm>
      </StbModal>

      {/* Confirm delete modal */}
      <StbModal
        open={confirmOpen}
        role="delete"
        onClose={cancelDelete}
        title="Confirm Delete"
        submitLabel="Confirm"
        onSubmit={confirmDelete}
      >
        <div style={{ padding: '0.25rem 0 1rem' }}>
          Are you sure you want to delete this agenda item?
        </div>
      </StbModal>
    </div>
  );
}